import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import TicketBoard, { colorOf, mainText, short, countLegs } from './TicketBoard';
import { ticketLabel } from './SharedPicks';
import { sortedLegs, legKey, missingLegs, isVoid, blinkClass } from './ticketDate';

const ROW = {
  neutral: 'border-l-zinc-600 text-zinc-300',
  yellow: 'border-l-yellow-400 text-yellow-200',
  orange: 'border-l-orange-500 bg-orange-500/10 text-orange-200',
  blue: 'border-l-sky-400 bg-sky-500/10 text-sky-200',
  purple: 'border-l-purple-400 bg-purple-500/10 text-purple-200',
  green: 'border-l-emerald-400 bg-emerald-500/15 text-emerald-200',
  red: 'border-l-red-500 bg-red-500/15 text-red-200',
};
const usd = (n) => (n ? `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—');
const legName = (l) => (l.player_name ? String(l.player_name).trim().split(/\s+/).filter((p) => !/^(jr|sr|ii|iii|iv)\.?$/i.test(p)).slice(1).join(' ') || l.player_name : short(l.selection));
const rowColor = (l) => (l.availability && !['green', 'red'].includes(colorOf(l)) ? 'orange' : colorOf(l));

const result = (t) => {
  const legs = t.legs || [];
  const c = countLegs(legs);
  const hit = legs.filter((l) => colorOf(l) === 'green').length;
  const voided = legs.filter(isVoid).length;
  const lost = t.manual_result ? t.manual_result === 'lost' : c.lost > 0 || t.status === 'lost';
  const won = t.manual_result ? t.manual_result === 'won' : !missingLegs(t) && !lost && hit > 0 && hit + voided === legs.length;
  return { hit, n: legs.length, lost, won };
};

const MiniCard = ({ t, onOpen }) => {
  const r = result(t);
  return (
    <button type="button" onClick={onOpen} data-testid={`mini-ticket-${t.id}`}
      className={`min-w-0 rounded-md border p-1 text-left transition-transform active:scale-[0.98] ${r.lost ? 'border-red-500/60 bg-red-950/30' : r.won ? 'border-emerald-400/70 bg-emerald-950/30' : 'border-zinc-700 bg-zinc-900/70'}`}>
      <p className="truncate text-[10px] font-bold text-white" title={ticketLabel(t)}>{ticketLabel(t)}</p>
      <div className="flex items-center justify-between gap-1 text-[8px] leading-tight">
        <span className="truncate text-zinc-400">{usd(t.stake)}→<b className="text-emerald-300">{usd(t.potential_payout)}</b></span>
        <span className={`shrink-0 font-black ${r.lost ? 'text-red-400' : r.won ? 'text-emerald-300' : 'text-sky-300'}`} data-testid={`mini-hits-${t.id}`}>
          {r.lost ? 'LOST' : r.won ? 'WON' : `${r.hit}/${r.n}`}
        </span>
      </div>
      <ul className="mt-0.5 space-y-px">
        {sortedLegs(t.legs || []).map(([l, i]) => (
          <li key={legKey(l, i)} className={`truncate rounded-sm border-l-2 px-0.5 font-mono text-[9px] leading-[13px] ${ROW[rowColor(l)] || ROW.neutral}${blinkClass(l.blink, colorOf(l))}`}
            data-testid={`mini-leg-${t.id}-${i}`} data-color={rowColor(l)} title={l.availability?.text || l.selection}>
            <span className="font-sans font-semibold">{legName(l)}</span> {mainText(l)}{l.availability ? ' ⚠' : ''}
          </li>
        ))}
      </ul>
    </button>
  );
};

// Compact view: many small tickets per row, one line per leg. Tap a ticket for the full card.
export default function MiniBoard({ tickets, onDelete, onArchive, onSettle, onChanged }) {
  const [openId, setOpenId] = useState(null);
  const open = tickets.find((t) => t.id === openId);
  return (
    <>
      <div className="grid grid-cols-3 gap-1 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 items-start" data-testid="mini-board">
        {tickets.map((t) => <MiniCard key={t.id} t={t} onOpen={() => setOpenId(t.id)} />)}
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpenId(null)}>
        <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto border-zinc-800 bg-zinc-950 p-3 pt-10" data-testid="mini-ticket-dialog">
          <DialogTitle className="sr-only">Ticket</DialogTitle>
          {open && <TicketBoard tickets={[open]} layout="single" onDelete={(t) => { setOpenId(null); onDelete(t); }} onArchive={onArchive} onSettle={onSettle} onChanged={onChanged} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
